import dayjs from 'dayjs';
import { showSnackbar } from '../../../Reducer/SnackbarReducer';
import { setRowData } from '../../../Reducer/GridReducer';

const maxFileSize = 50 * 1024 * 1024; // 50MB
const extensionMap = {
  docx: {
    exts: ['docx'],
    mime: 'application/docx',
    message: '워드(docx) 파일만 업로드 가능합니다.',
  },

  xls: {
    exts: ['xls', 'xlsx', 'cell'],
    mime: 'application/xls',
    message: '엑셀 파일만 업로드 가능합니다.',
  },
  pdf: {
    exts: ['pdf'],
    mime: 'application/pdf',
    message: 'PDF 파일만 업로드 가능합니다.',
  },
  hwp: {
    exts: ['hwp', 'hwpx'],
    mime: 'application/hwp',
    message: '한글 파일만 업로드 가능합니다.',
  },
  img: {
    exts: ['jpeg', 'jpg', 'png'],
    mime: 'image/png',
    message: 'JPEG 또는 PNG 파일만 업로드 가능합니다.',
  },
  video: {
    exts: ['mp4'],
    mime: 'video/mp4', // 변경 필요
    message: '비디오 파일만 업로드 가능합니다.',
  },
  custom: {
    exts: ['pdf', 'hwp', 'hwpx', 'jpg', 'jpeg', 'png'],
    mime: '*/*',
    message: 'PDF, 이미지(jpg, png), 한글(hwp)만 업로드 가능합니다.',
  },
};

// 파일을 Base64로 변환하는 함수
const FileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export const ClickGridCellFileUploadFunction = (
  event,
  gridState,
  gridRef,
  e,
  extension,
  props,
  isUploading,
  setIsUploading,
  options = {}
) => {
  const { blockExcel = false, blockPpt = false, blockDocx = false } = options;
  if (isUploading) return;
  setIsUploading(true);

  const input = document.createElement('input');
  input.type = 'file';
  const { exts } = extensionMap[extension] || {};
  input.accept = exts?.map((ext) => `.${ext}`).join(',') || '';
  input.style.display = 'none';

  input.onchange = (event) => {
    GridCellFileUploadFunction(event, gridState, gridRef, e, props, extension, {
      blockExcel,
      blockPpt,
      blockDocx,
    });
  };

  document.body.appendChild(input);
  input.click();

  setTimeout(() => {
    document.body.removeChild(input);
    setIsUploading(false);
  }, 1000);
};

export const GridCellFileUploadFunction = async (
  event,
  gridState,
  gridRef,
  e,
  props,
  extension,
  options = {}
) => {
  const { blockExcel = false, blockPpt = false, blockDocx = false } = options;
  const file = event?.target?.files?.[0];

  if (!file) return;

  if (!e?.node?.id) {
    props.dispatch(
      showSnackbar({
        message: '파일을 등록할 수 없습니다. 다시 시도해 주세요.',
        type: 'warning',
      })
    );
    return;
  }

  const invalidCharRegex = /[^\w가-힣\s().\[\]_,&-]/g; // 영문/숫자/한글/공백/( ) . _ -[ ] , &

  const ext = file.name.split('.').pop()?.toLowerCase();

  if (blockExcel && ['xls', 'xlsx', 'cell'].includes(ext)) {
    props.dispatch(
      showSnackbar({
        message: `엑셀 파일은 업로드할 수 없습니다.`,
        type: 'warning',
      })
    );
    return;
  }

  if (blockPpt && ['ppt', 'pptx'].includes(ext)) {
    props.dispatch(
      showSnackbar({
        message: `파워포인트 파일은 업로드할 수 없습니다.`,
        type: 'warning',
      })
    );
    return;
  }

  if (blockDocx && ['doc', 'docx'].includes(ext)) {
    props.dispatch(
      showSnackbar({
        message: `워드 파일은 업로드할 수 없습니다.`,
        type: 'warning',
      })
    );
    return;
  }

  const { exts, message } = extensionMap[extension] || {};
  if (exts && !exts.includes(ext)) {
    props.dispatch(
      showSnackbar({
        message:
          extension === 'custom'
            ? `허용되지 않는 형식입니다. PDF, 이미지(jpg, png), 한글(hwp)만 업로드 가능합니다.`
            : message || '허용되지 않는 파일 형식입니다.',
        type: 'warning',
      })
    );
    return;
  }
  if (invalidCharRegex.test(file.name)) {
    props.dispatch?.(
      showSnackbar({
        message: `파일명에 특수문자가 포함되어 있습니다: ${file.name}`,
        type: 'warning',
      })
    );
    return;
  }
  // if (file.size > maxFileSize) {
  //   props.dispatch?.(
  //     showSnackbar({
  //       message: `파일 용량이 50MB를 초과합니다: ${file.name}`,
  //       type: 'warning',
  //     })
  //   );
  //   return;
  // }

  const reader = new FileReader();

  reader.onload = async (readerEvent) => {
    // const mimeType =
    //   extensionMap[extension]?.mime || `application/${extension}`;
    // const blob = new Blob([readerEvent.target.result], { type: mimeType });
    // const timestamp = dayjs().format('YYYYMMDDHHmmss');
    // const saveName = `${timestamp}.${extension}`;

    // const fileToSave = new File([blob], saveName, { type: mimeType });

    const mimeType = file.type || 'application/octet-stream';
    const blob = new Blob([readerEvent.target.result], { type: mimeType });
    const timestamp = dayjs().format('YYYYMMDDHHmmss');
    const saveName = `${timestamp}_${file.name}`;

    const fileToSave = new File([blob], saveName, { type: mimeType });

    props.setPdfFile?.({
      mapBlob: blob,
      mapFile: fileToSave,
      mapFileName: file.name,
      mapSaveName: saveName,
    });

    if (gridRef.current) {
      const { api } = gridRef.current;
      const rowNode = api.getRowNode(e.node.id);

      // 미리보기 파일 저장
      const previewPath = (await props.SaveLogicPreview?.(fileToSave)) || '';

      const updatedData = {
        ...rowNode.data,
        date: rowNode.data.date || props.searchValue?.endDate,
        mapBlob: blob,
        mapFile: fileToSave,
        mapFileName: file.name,
        fileName: file.name,
        FILE_NAME: file.name,
        saveName: saveName,
        SAVE_NAME: saveName,
        previewFilePath: previewPath,
        PREVIEW_FILE_PATH: previewPath,
        editYn: 'Y',
      };
      rowNode.setData(updatedData);
      api.deselectAll();
      rowNode.setSelected(true, true);

      props.setSelectedRowData1?.([{ ...updatedData }]);
      props.setSelectedRowData?.([{ ...updatedData }]);
      const result = [
        ...props.changedRowData1.filter((row) => row.no !== updatedData.no),
        { ...updatedData },
      ];
      props.setChangedRowData1?.(result);
      rowNode.setSelected(true);

      if (gridState) {
        props.dispatch(
          setRowData({
            gridId: 'grid1',
            rowData: gridState.rowData.map((row) =>
              row.no === updatedData.no ? { ...updatedData, editYn: 'Y' } : row
            ),
          })
        );
      }
    }
  };

  reader.onerror = (error) => {
    console.error('FileReader error:', error);
    props.dispatch(
      showSnackbar({
        message: '파일을 읽는 중 오류가 발생했습니다.',
        type: 'error',
      })
    );
  };

  reader.readAsArrayBuffer(file);

  event.target.value = '';
};
